import React, { useEffect } from "react";
import { LogOut, RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import menuService from "@/services/menuService";
import { cn } from "@/lib/utils";
import StoreStatusBadge from "./StoreStatusBadge";

/**
 * Menu do gestor (itens de GET /menus, já filtrados pelos perfis do usuário).
 * - Celular/tablet: gaveta lateral sobre o conteúdo (abre pelo Header ou
 *   pelo "Menu" da barra inferior).
 * - Desktop (lg+): coluna fixa à esquerda.
 */
const Sidebar = ({
  menuItems,
  menusLoading,
  menusError,
  onRetry,
  activeTab,
  onSelect,
  open,
  onClose,
}) => {
  const { user, logout, company } = useAuth();

  // Fecha a gaveta com Esc
  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 z-40 bg-[#1f2e1e]/40 backdrop-blur-sm transition-opacity duration-300 lg:hidden",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-[84%] max-w-[320px] flex-col border-r bg-card shadow-ambient transition-transform duration-300",
          "lg:sticky lg:top-16 lg:z-20 lg:h-[calc(100vh-4rem)] lg:w-64 lg:max-w-none lg:translate-x-0 lg:shadow-none",
          open ? "translate-x-0" : "-translate-x-full",
        )}
        aria-label="Menu do gestor"
      >
        {/* Cabeçalho da gaveta (só no celular) */}
        <div className="flex h-16 items-center justify-between border-b px-4 lg:hidden">
          <div className="flex items-center gap-2">
            <img
              src="/assets/logo_cardapio.jpg"
              alt="Cardápio Bonfim"
              className="h-8 w-8 rounded-full bg-white object-contain"
            />
            <span className="font-display text-lg font-semibold">
              Menu do Gestor
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar menu"
            className="flex h-11 w-11 items-center justify-center rounded-xl border bg-surface text-muted-foreground transition-colors hover:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-1 overflow-y-auto px-3 py-3 lg:px-4 lg:py-5">
          {menusLoading ? (
            <div className="space-y-2" aria-label="Carregando menu">
              {Array.from({ length: 7 }, (_, i) => (
                <div key={i} className="h-11 animate-pulse rounded-xl bg-muted" />
              ))}
            </div>
          ) : menusError ? (
            <div className="space-y-3 px-2 py-6 text-center">
              <p className="text-sm text-muted-foreground">
                Não foi possível carregar o menu.
              </p>
              <Button variant="outline" size="sm" onClick={onRetry}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Tentar novamente
              </Button>
            </div>
          ) : (
            <nav className="space-y-1">
              {menuItems.map((item) => {
                const Icon = menuService.resolveIcon(item.icon);
                const active = activeTab === item.link;
                return (
                  <button
                    key={item.link}
                    type="button"
                    onClick={() => onSelect(item.link)}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold transition-colors",
                      active
                        ? "bg-secondary text-primary"
                        : "text-foreground hover:bg-surface",
                    )}
                  >
                    <Icon
                      className={cn(
                        "h-5 w-5 flex-shrink-0",
                        active ? "text-primary" : "text-muted-foreground",
                      )}
                    />
                    <span className="truncate">{item.label}</span>
                  </button>
                );
              })}
            </nav>
          )}
        </div>

        {/* Rodapé: status da loja e sessão */}
        <div className="space-y-3 border-t bg-surface px-4 py-4 pb-safe">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-foreground">
              Status da Loja
            </span>
            <StoreStatusBadge company={company} />
          </div>
          <div className="flex items-center justify-between gap-2">
            <span
              className="truncate text-xs text-muted-foreground"
              title={user?.email}
            >
              {user?.email}
            </span>
            <button
              type="button"
              onClick={logout}
              className="flex flex-shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-destructive transition-colors hover:bg-destructive/10"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sair
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
